import { useState, useEffect } from "react";
import { BuzerIcon } from "./BuzerIcons";
import {
  Button, IconButton, Tag, Segmented, Eyebrow, Numeral,
  List, ListRow, Lead, CheckButton, GroupCard, Fab,
} from "./components/ui/ds";

// Faithful reconstruction of the "Dnes - Nový" mock, built entirely from v2
// primitives — dev preview at #dnes to approve the look before migrating the
// real BuzerFeature. Theme/dark switcher at the top.

const THEMES = [
  ["green", "#6E7E5E"], ["indigo", "#4A4FA8"], ["cobalt", "#0A4DFF"],
  ["violet", "#7C3AED"], ["terracotta", "#C2552F"], ["teal", "#1F8A7D"],
];
const dot = { width: 3, height: 3, borderRadius: 99, background: "var(--neutral-text-subtle)" };

function meta(points, extra) {
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
      <Numeral style={{ fontSize: 11, color: "var(--brand-primary)" }}>{points}</Numeral>
      {extra && <><span style={dot} /><span style={{ fontSize: 11.5, color: "var(--neutral-text-muted)" }}>{extra}</span></>}
    </span>
  );
}
const habitLead = (icon) => <Lead tone="outline">{<BuzerIcon name={icon} size={17} color="currentColor" />}</Lead>;
const AddPair = ({ a, b }) => (
  <div style={{ display: "flex", gap: 10 }}>
    {[a, b].map((label) => (
      <Button key={label} variant="outline" style={{ flex: 1 }}>
        <BuzerIcon name="add" size={17} color="currentColor" strokeWidth={2.2} />{label}
      </Button>
    ))}
  </div>
);

export default function DnesPreview() {
  const [theme, setTheme] = useState("indigo");
  const [dark, setDark] = useState(false);

  // Apply the previewed theme/mode to <html> (mount + on change) so the swatch
  // selection matches what's rendered, regardless of the app's boot default.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    document.documentElement.classList.toggle("dark", dark);
  }, [theme, dark]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--neutral-bg)", color: "var(--neutral-text-main)", fontFamily: "var(--ff-ui)" }}>
      <header style={{ position: "sticky", top: 0, zIndex: 40, background: "color-mix(in srgb, var(--neutral-surface) 82%, transparent)", backdropFilter: "blur(12px)", borderBottom: "1px solid var(--neutral-border)", padding: "10px 16px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}> /* mali-ok: dev preview, nie v user flow */
        <Eyebrow style={{ color: "var(--brand-primary)" }}>Dnes · nový</Eyebrow>
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", gap: 6 }}>
          {THEMES.map(([t, hex]) => (
            <button key={t} onClick={() => setTheme(t)} title={t}
              style={{ width: 20, height: 20, borderRadius: 99, background: hex, cursor: "pointer", padding: 0,
                border: theme === t ? "2px solid var(--neutral-surface)" : "2px solid transparent",
                outline: theme === t ? "2px solid var(--neutral-text-main)" : "none" }} />
          ))}
        </div>
        <Segmented options={[{ value: false, label: "Svetlý" }, { value: true, label: "Tmavý" }]} value={dark} onChange={setDark} />
      </header>

      <div style={{ maxWidth: 390, margin: "0 auto", padding: "14px 16px 120px", position: "relative" }}>
        {/* header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <span style={{ fontFamily: "var(--ff-display)", fontWeight: 600, fontSize: 30, letterSpacing: "-.02em" }}>Dnes.</span>
            <Tag tone="streak" icon="flame">11%</Tag>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <IconButton variant="surface" icon="bell" aria-label="Upozornenia" />
            <IconButton variant="surface" icon="grid" aria-label="Menu" />
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", margin: "10px 2px" }}>
          <span style={{ fontFamily: "var(--ff-mono)", fontSize: 10, letterSpacing: ".1em", color: "var(--brand-primary)" }}>TABUĽKA ›</span>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* routine 1 — done */}
          <GroupCard title="Ranná rutina" count="1/1" pct={100} onAdd={() => {}}>
            <ListRow lead={habitLead("glass")}
              title={<span style={{ color: "var(--neutral-text-subtle)" }}>Pohár vody ráno</span>}
              meta={meta("+15")}
              trailing={<CheckButton done onClick={() => {}} />} />
          </GroupCard>

          {/* routine 2 — 0/2 */}
          <GroupCard icon="flame" title="Go girl" count="0/2" pct={0} onAdd={() => {}}>
            <ListRow lead={habitLead("dumbbell")} title="Visenie na hrazde" meta={meta("+15", "1 min")}
              trailing={<CheckButton onClick={() => {}} />} />
            <ListRow lead={habitLead("dumbbell")} title="1 zdvih na hrazde" meta={meta("+15")}
              trailing={<CheckButton onClick={() => {}} />} />
          </GroupCard>

          {/* standalone habit */}
          <List>
            <ListRow lead={habitLead("book")} title="Prečítať 10 strán" meta={meta("+20", "denne")}
              trailing={<CheckButton onClick={() => {}} />} />
          </List>

          <AddPair a="Nová skupina" b="Nový návyk" />

          <Eyebrow style={{ margin: "2px 2px" }}>Výzvy a súťaže</Eyebrow>
          <List>
            <ListRow lead={<Lead tone="brand"><BuzerIcon name="leaf" size={17} color="currentColor" /></Lead>}
              title={<span style={{ display: "flex", alignItems: "center", gap: 6 }}>Kombucha <Tag tone="brand" size="xs">VÝZVA</Tag></span>}
              meta={meta("+5")}
              trailing={<CheckButton onClick={() => {}} />} />
          </List>

          <AddPair a="Nová výzva" b="Nová súťaž" />

          <div style={{ textAlign: "center", padding: "18px 0 4px", fontFamily: "var(--ff-mono)", fontSize: 10.5, letterSpacing: ".08em", color: "var(--neutral-text-subtle)" }}>
            · nálada · AI asistent · štatistiky pokračujú nižšie ·
          </div>
        </div>

        <Fab onClick={() => {}} style={{ position: "absolute", right: 16, bottom: 24 }} />
      </div>
    </div>
  );
}
